import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderSelector } from './FolderSelector';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { MoveViewModelConfirmModal } from './MoveViewModelConfirmModal';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
};

export const MoveViewModelModal: React.FC<Props> = ({ isOpen, onClose, viewModel }: Props) => {
  const { t } = useTranslation();
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

  const handleClose = useCallback(() => {
    setSelectedFolder(null);
    setProcessing(false);
    onClose();
  }, [onClose]);

  const [processing, setProcessing] = useState(false);

  const handleMove = useCallback(async () => {
    if (!selectedFolder) return;
    setProcessing(true);
  }, [selectedFolder]);

  return (
    <>
      <GeneralModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('workspace.moveViewModel.title')}
        submitButton={
          <Button onClick={handleMove} disabled={!selectedFolder} loading={processing}>
            {t('workspace.modal.move')}
          </Button>
        }
        cancelButton={
          <Button onClick={handleClose} color="secondary">
            {t('common.action.cancel')}
          </Button>
        }
      >
        <div className="p-2" style={{ width: '600px' }}>
          {t('workspace.moveViewModel.description', { name: viewModel.name.value })}
          <hr className="mt-2" />
          {isOpen && (
            <FolderSelector
              workspaceId={viewModel.workspaceId}
              itemId={viewModel.id}
              onChangeSelectedFolder={setSelectedFolder}
            />
          )}
        </div>
      </GeneralModal>

      {selectedFolder && processing && (
        <MoveViewModelConfirmModal
          isOpen={true}
          onClose={() => setProcessing(false)}
          viewModel={viewModel}
          destinationFolder={selectedFolder}
        />
      )}
    </>
  );
};
